/**
 * Checkpoint Frequency Master — admin page.
 *
 * Route: /production/checkpoint-frequency
 *
 * Configure how many checkpoints each department runs per shift.
 * Seeded defaults: prep depts (Blow Room / Carding / Drawing / Comber /
 * Simplex / Packing) = 360 min × 2; Ring Frame / Winding / Autoconer /
 * Link Coner = 120 min × 6.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, DatePicker, Drawer, Form, Input, InputNumber, Select,
  Space, Switch, Table, Tag, Typography, message,
} from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { checkpointsApi, type FrequencyMaster } from '@/features/production/checkpointsApi'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

const DEPT_CODES = [
  'BLOWROOM', 'CARDING', 'DRAWING', 'COMBING', 'SIMPLEX',
  'RING_FRAME', 'WINDING', 'AUTOCONER', 'LINK_CONER', 'PACKING',
]

export default function CheckpointFrequencyPage() {
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()

  const listQ = useQuery({
    queryKey: ['cp-frequencies', plantId],
    queryFn: () => checkpointsApi.listFrequencies({
      plant_id: plantId, active_only: false,
    }),
  })

  const createMut = useMutation({
    mutationFn: (body: any) => checkpointsApi.createFrequency(body),
    onSuccess: () => {
      message.success('Frequency added')
      setCreateOpen(false)
      form.resetFields()
      qc.invalidateQueries({ queryKey: ['cp-frequencies'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      checkpointsApi.updateFrequency(id, { active }),
    onSuccess: () => {
      message.success('Updated')
      qc.invalidateQueries({ queryKey: ['cp-frequencies'] })
    },
  })

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Checkpoint Frequency Master</Title>
      <Text type="secondary">
        How many checkpoints each department runs per shift. Plant-wide
        rows (plant blank) are fallback defaults; plant-specific rows
        override them.
      </Text>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            New Frequency
          </Button>
        </Space>
      </Card>

      <Table<FrequencyMaster>
        size="small"
        rowKey="id"
        loading={listQ.isLoading}
        dataSource={listQ.data ?? []}
        pagination={{ pageSize: 50 }}
        columns={[
          {
            title: 'Department',
            dataIndex: 'department_code',
            key: 'department_code',
            sorter: (a, b) => a.department_code.localeCompare(b.department_code),
          },
          {
            title: 'Machine Type',
            dataIndex: 'machine_type',
            key: 'machine_type',
            render: (v: string | null) => v ?? <Text type="secondary">(any)</Text>,
          },
          {
            title: 'Plant',
            dataIndex: 'plant_id',
            key: 'plant_id',
            render: (v: string | null) => v ? (
              <Tag color="blue">Plant-specific</Tag>
            ) : (
              <Tag>Default</Tag>
            ),
          },
          {
            title: 'Interval (min)',
            dataIndex: 'checkpoint_interval_minutes',
            key: 'interval',
            align: 'right',
          },
          {
            title: 'Per shift',
            dataIndex: 'checkpoints_per_shift',
            key: 'per_shift',
            align: 'right',
          },
          {
            title: 'From',
            dataIndex: 'effective_from',
            key: 'from',
            render: (v: string) => dayjs(v).format('DD MMM YY'),
          },
          {
            title: 'To',
            dataIndex: 'effective_to',
            key: 'to',
            render: (v: string | null) => v ? dayjs(v).format('DD MMM YY') : '-',
          },
          {
            title: 'Seeded',
            dataIndex: 'system_seeded',
            key: 'seeded',
            render: (v: boolean) => v ? <Tag>System</Tag> : <Tag color="cyan">Admin</Tag>,
          },
          {
            title: 'Active',
            dataIndex: 'active',
            key: 'active',
            render: (v: boolean, r) => (
              <Switch
                checked={v}
                size="small"
                onChange={(checked) => toggleMut.mutate({ id: r.id, active: checked })}
              />
            ),
          },
        ]}
      />

      <Drawer
        title="New Checkpoint Frequency"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={480}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            effective_from: dayjs(),
            checkpoint_interval_minutes: 120,
            checkpoints_per_shift: 6,
            start_from_shift_start: true,
          }}
          onFinish={(v) => createMut.mutate({
            plant_id: v.plant_specific ? plantId : null,
            department_code: v.department_code,
            machine_type: v.machine_type || null,
            checkpoint_interval_minutes: v.checkpoint_interval_minutes,
            checkpoints_per_shift: v.checkpoints_per_shift,
            start_from_shift_start: v.start_from_shift_start,
            effective_from: (v.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
            effective_to: v.effective_to
              ? (v.effective_to as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            notes: v.notes,
          })}
        >
          <Form.Item name="department_code" label="Department" rules={[{ required: true }]}>
            <Select options={DEPT_CODES.map((d) => ({ value: d, label: d }))} />
          </Form.Item>
          <Form.Item name="machine_type" label="Machine Type (optional sub-filter)">
            <Input placeholder="e.g. LMW-LC363 (leave blank for all machines in dept)" />
          </Form.Item>
          <Form.Item name="plant_specific" valuePropName="checked">
            <Space>
              <Switch />
              <Text>Plant-specific (otherwise applies to all plants)</Text>
            </Space>
          </Form.Item>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item
              name="checkpoint_interval_minutes"
              label="Interval (min)"
              rules={[{ required: true }]}
            >
              <InputNumber min={5} max={720} style={{ width: 140 }} />
            </Form.Item>
            <Form.Item
              name="checkpoints_per_shift"
              label="Per shift"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={48} style={{ width: 140 }} />
            </Form.Item>
          </Space>
          <Form.Item name="start_from_shift_start" valuePropName="checked">
            <Space>
              <Switch defaultChecked />
              <Text>Align to shift start (otherwise wall-clock hours)</Text>
            </Space>
          </Form.Item>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="effective_from" label="From" rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="effective_to" label="To (optional)">
              <DatePicker />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={createMut.isPending}>
              Create
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  )
}
